import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

export default function DistributionHistogram({ 
  data = [], 
  dataKey = 'value', 
  bins = 20,
  xLabel, 
  yLabel = 'Frequency',
  title,
  showMean = true,
  showMedian = false,
  color = '#3b82f6'
}) {
  // Validate data
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p>No data available for histogram</p>
      </div>
    );
  }
  // Calculate histogram bins
  const histogramData = calculateHistogram(data, dataKey, bins);
  
  // Calculate statistics
  const stats = calculateStats(data, dataKey);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const bin = payload[0].payload;
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
          <p className="text-slate-300 text-sm">
            <span className="font-semibold">Range:</span> {bin.rangeLabel}
          </p>
          <p className="text-slate-300 text-sm">
            <span className="font-semibold">Count:</span> {bin.count}
          </p>
          <p className="text-slate-400 text-xs mt-1">
            {((bin.count / stats.total) * 100).toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full">
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
            <span>Mean: <span className="font-semibold text-slate-300">{stats.mean.toFixed(2)}</span></span>
            <span>Median: <span className="font-semibold text-slate-300">{stats.median.toFixed(2)}</span></span>
            <span>Std Dev: <span className="font-semibold text-slate-300">{stats.stdDev.toFixed(2)}</span></span>
            <span>n = {stats.total}</span>
          </div>
        </div>
      )}
      
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={histogramData} margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis 
            dataKey="rangeLabel"
            label={{ value: xLabel || dataKey, position: 'insideBottom', offset: -10, fill: '#94a3b8' }}
            stroke="#64748b"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#94a3b8' }}
            stroke="#64748b"
            tick={{ fill: '#94a3b8' }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          
          {/* Mean line */}
          {showMean && (
            <ReferenceLine 
              x={findBinForValue(histogramData, stats.mean)}
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: 'Mean', fill: '#10b981', fontSize: 12, position: 'top' }}
            />
          )}
          
          {/* Median line */}
          {showMedian && (
            <ReferenceLine 
              x={findBinForValue(histogramData, stats.median)}
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: 'Median', fill: '#f59e0b', fontSize: 12, position: 'top' }}
            />
          )}
          
          <Bar 
            dataKey="count" 
            fill={color}
            name="Frequency"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Calculate histogram bins
function calculateHistogram(data, dataKey, bins) {
  if (!data || data.length === 0) return [];

  const values = data.map(d => parseFloat(d[dataKey])).filter(v => !isNaN(v));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const binWidth = (max - min) / bins;

  const histogram = Array(bins).fill(0).map((_, i) => ({
    binStart: min + i * binWidth,
    binEnd: min + (i + 1) * binWidth,
    count: 0
  }));

  values.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / binWidth), bins - 1);
    histogram[binIndex].count++;
  });

  return histogram.map(bin => ({
    ...bin,
    rangeLabel: `${bin.binStart.toFixed(1)}-${bin.binEnd.toFixed(1)}`
  }));
}

// Calculate statistics
function calculateStats(data, dataKey) {
  const values = data.map(d => parseFloat(d[dataKey])).filter(v => !isNaN(v)).sort((a, b) => a - b);
  
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const median = values.length % 2 === 0 
    ? (values[values.length / 2 - 1] + values[values.length / 2]) / 2
    : values[Math.floor(values.length / 2)];
  
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);

  return {
    mean,
    median,
    stdDev,
    min: values[0],
    max: values[values.length - 1],
    total: values.length
  };
}

// Find bin label for a specific value
function findBinForValue(histogramData, value) {
  const bin = histogramData.find(b => value >= b.binStart && value <= b.binEnd);
  return bin ? bin.rangeLabel : null;
}
